import { Hideable } from "shared/UI/Hideable/Hideable";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import Button from "shared/UI/Buttons/Button";
import { TrackerProgressDonut } from "UIPalette/TrackerProgress/TrackerProgress";
import { TrackerType } from "core/state/Trackers/TrackerType";
import { ComponentType, useState } from "react";
import { useTranslation } from "react-i18next";
import { SetGoalModal } from "./SetGoalModal";
import { trackBtnClicks, trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { useTrackerContext } from "core/state/TrackerContext/TrackerContext";

export type TrackerProgressCardProps = {
  trackerType: TrackerType;
  percentage: number;
  heading: string;
  goalHeading: string;
  goalDescription: string;
  goalIncrement: number;
  goalUnit: string;
  progressMessage: string;
  Icon: ComponentType<React.SVGProps<SVGSVGElement>>;
};

export const TrackerProgressCard = ({
  trackerType,
  percentage,
  heading,
  goalHeading,
  goalDescription,
  goalUnit,
  goalIncrement,
  progressMessage,
  Icon,
}: TrackerProgressCardProps) => {
  const { t } = useTranslation();
  const [showSetGoal, setShowSetGoal] = useState(false);
  const { getGoal, setTrackerGoal } = useTrackerContext();

  function save(goal: number) {
    trackEvents(EventNames.SAVE_HEALTH_DATA_GOAL, { value: TrackerType[trackerType] });
    setTrackerGoal(trackerType, goal);
    setShowSetGoal(false);
  }

  const handleOpenModal = () => {
    trackBtnClicks(`change_${TrackerType[trackerType]}_goal_data`);
    setShowSetGoal(true);
  };

  const handleCloseModal = () => {
    trackBtnClicks(`cancel_change_${TrackerType[trackerType]}_goal_data`);
    setShowSetGoal(false);
  };

  return (
    <>
      <RoundedCard>
        <div className="flex flex-col gap-y-4">
          <h2 className="p-0">{heading}</h2>
          <div className="flex flex-row gap-x-4 items-center">
            <div className="w-16 h-16 flex-shrink-0">
              <TrackerProgressDonut percentage={percentage} size="sm" TrackerIcon={Icon} />
            </div>
            <p role="note">{progressMessage}</p>
          </div>
          <Button text={t("wellbeing.trackers.goal.buttons.changeGoal")} onClick={handleOpenModal} />
        </div>
      </RoundedCard>
      <Hideable hidden={!showSetGoal}>
        <SetGoalModal
          heading={goalHeading}
          description={goalDescription}
          unit={goalUnit}
          incrementStep={goalIncrement}
          currentGoal={getGoal(trackerType)}
          onClose={handleCloseModal}
          onSave={save}
        />
      </Hideable>
    </>
  );
};
